import { Routes } from "@angular/router";

// Guards
import { AuthGuard } from "./shared/guards/auth.guard";
import { GuestGuard } from "./shared/guards/guest.guard";
import { MembershipGuard } from "./core/guards/membership.guard";
import { MembershipRoleGuard } from "./core/guards/membership-role.guard";
import { MembershipTierGuard } from "./core/guards/membership-tier.guard";
import { MembershipRoles } from './core/data-models/membership-roles.model';

// Layout
import { LayoutComponent } from "./core/components/layout/layout.component";

// Pages
import { LoginComponent } from "./pages/login/login.component";
import { ResetPasswordComponent } from "./pages/reset-password/reset-password.component";
import { SignupComponent } from "./pages/signup/signup.component";
import { AcceptInvitationComponent } from "./pages/accept-invitation/accept-invitation.component";
import { DashboardComponent } from "./pages/dashboard/dashboard.component";
import { CompanyProfileComponent } from "./pages/company-profile/company-profile.component";
import { CertificationsComponent } from "./pages/certifications/certifications.component";
import { CertificationFormComponent } from "./pages/certifications/certification-form/certification-form.component";
import { UnauthorizedComponent } from "./pages/unauthorized/unauthorized.component";
import { AccountNotSupportedComponent } from "./pages/account-not-supported/account-not-supported.component";
import { AccountDeletionPageComponent } from "./pages/account-deletion/account-deletion-page.component";

// Team Page
import { TeamComponent } from "./pages/team/team.component";

// Profile Page
import { ProfileComponent } from "./pages/profile/profile.component";
import { DeleteAccountComponent } from "./pages/profile/delete-account/delete-account.component";

// Logbook Pages
import { LogbookOverviewComponent } from "./pages/logbook/logbook-overview/logbook-overview.component";
import { SprayRecordsComponent } from "./pages/logbook/spray-records/spray-records.component";
import { SprayRecordFormComponent } from "./pages/logbook/spray-records/spray-record-form/spray-record-form.component";
import { SprayRecordDetailsComponent } from "./pages/logbook/spray-records/spray-record-details/spray-record-details.component";
import { ChemicalBookComponent } from "./pages/logbook/chemicals/chemical-book/chemical-book.component";
import { CustomChemicalsComponent } from "./pages/logbook/chemicals/custom-chemicals/custom-chemicals.component";
import { CustomChemicalFormComponent } from "./pages/logbook/chemicals/custom-chemicals/custom-chemical-form/custom-chemical-form.component";
import { CustomChemicalDetailsComponent } from "./pages/logbook/chemicals/custom-chemicals/custom-chemical-details/custom-chemical-details.component";
import { ChemicalTemplatesComponent } from "./pages/logbook/chemicals/chemical-templates/chemical-templates.component";
import { ChemicalTemplateFormComponent } from "./pages/logbook/chemicals/chemical-templates/chemical-template-form/chemical-template-form.component";
import { ChemicalTemplateDetailsComponent } from "./pages/logbook/chemicals/chemical-templates/chemical-template-details/chemical-template-details.component";
import { SafetyTestsComponent } from "./pages/logbook/testing/safety-tests/safety-tests.component";
import { SafetyTestReportComponent } from "./pages/logbook/testing/safety-tests/safety-test-report/safety-test-report.component";
import { SafetyTestTakeComponent } from "./pages/logbook/testing/safety-tests/safety-test-take/safety-test-take.component";
import { CustomSafetyTestFormComponent } from "./pages/logbook/testing/safety-tests/custom-safety-test-form/custom-safety-test-form.component";
import { TeamReportComponent } from "./pages/logbook/testing/team-report/team-report.component";
import { FieldTestsComponent } from "./pages/logbook/testing/field-tests/field-tests.component";
import { FieldTestFormComponent } from "./pages/logbook/testing/field-tests/field-test-form/field-test-form.component";
import { FieldTestDetailsComponent } from "./pages/logbook/testing/field-tests/field-test-details/field-test-details.component";
import { CustomFieldTestFormComponent } from "./pages/logbook/testing/field-tests/custom-field-test-form/custom-field-test-form.component";
import { CustomFieldTestDetailsComponent } from "./pages/logbook/testing/field-tests/custom-field-test-details/custom-field-test-details.component";
import { FaaReportsComponent } from "./pages/logbook/reports/faa-reports/faa-reports.component";
import { StateReportsComponent } from "./pages/logbook/reports/state-reports/state-reports.component";
import { FieldsComponent } from "./pages/logbook/fields/fields.component";
import { FieldFormComponent } from "./pages/logbook/fields/field-form/field-form.component";

// Customer Pages
import { CustomersComponent } from "./pages/customers/customers.component";
import { CustomerFormComponent } from "./pages/customers/customer-form/customer-form.component";
import { CustomerDetailsComponent } from "./pages/customers/customer-details/customer-details.component";

// Drone Pages
import { DronesComponent } from "./pages/drones/drones.component";
import { DroneFormComponent } from "./pages/drones/drone-form/drone-form.component";
import { DroneDetailsComponent } from "./pages/drones/drone-details/drone-details.component";

// Work Order Pages — gated to Owner + Chief Supervisor (org-scoped roles)
import { WorkOrdersComponent } from "./pages/work-orders/work-orders.component";
import { WorkOrderFormComponent } from "./pages/work-orders/work-order-form/work-order-form.component";
import { WorkOrderDetailsComponent } from "./pages/work-orders/work-order-details/work-order-details.component";

// Activity Page
import { ActivityComponent } from "./pages/activity/activity.component";

// Maintenance Pages
import { MaintenanceDashboardComponent } from "./pages/maintenance/maintenance-dashboard/maintenance-dashboard.component";
import { SupportTicketsComponent } from "./pages/maintenance/support-tickets/support-tickets.component";
import { SupportTicketFormComponent } from "./pages/maintenance/support-tickets/support-ticket-form/support-ticket-form.component";
import { ServiceAppointmentsComponent } from "./pages/maintenance/service-appointments/service-appointments.component";
import { ServiceAppointmentDetailComponent } from "./pages/maintenance/service-appointments/service-appointment-detail/service-appointment-detail.component";
import { ServiceAppointmentFormComponent } from "./pages/maintenance/service-appointments/service-appointment-form/service-appointment-form.component";
import { ScheduledMaintenanceComponent } from "./pages/maintenance/scheduled-maintenance/scheduled-maintenance.component";
import { TrainingVideosComponent } from "./pages/maintenance/training-videos/training-videos.component";
import { MaintenancePreviewComponent } from "./pages/maintenance/preview/maintenance-preview.component";

// Ac Advantage Page
import { AcresAdvantageComponent } from "./pages/acres-advantage/acres-advantage.component";

// Agremo Analysis Pages
import { AnalysesComponent } from "./pages/analyses/analyses.component";
import { AnalysisFormComponent } from "./pages/analysis-form/analysis-form.component";
import { AnalysisCheckoutReturnComponent } from "./pages/analysis-form/analysis-checkout-return.component";
import { AnalysisDetailComponent } from "./pages/analysis-detail/analysis-detail.component";
import { AgremoPreviewComponent } from "./pages/analyses/preview/agremo-preview.component";

// Billing Pages (Core vs Pro membership)
import { UpgradeToProComponent } from "./pages/billing/upgrade/upgrade-to-pro.component";
import { ManageSubscriptionComponent } from "./pages/billing/manage/manage-subscription.component";
import { SubscriptionCheckoutReturnComponent } from "./pages/billing/subscription-checkout-return.component";
import { AuthCodeComponent } from "./pages/auth-code/auth-code.component";
import { AuthCodeSuccessComponent } from "./pages/auth-code-success/auth-code-success.component";
import { AuthMethod } from './core/data-models/auth-method.model';
import { AuthMode } from './core/data-models/auth-mode.model';
import { ResetOptionsComponent } from "./pages/reset-options/reset-options.component";
export const routes: Routes = [
    // Guest routes (login, signup, etc.)
    {
        path: 'login',
        // On the parent so /login/sms and /login/email are guest-only too.
        canActivate: [GuestGuard],
        children: [
            {
                path: '',
                component: LoginComponent,
            },
            {
                path: 'sms',
                component: AuthCodeComponent,
                data: { mode: AuthMode.login, method: AuthMethod.sms }
            },
            {
                path: 'email',
                component: AuthCodeComponent,
                data: { mode: AuthMode.login, method: AuthMethod.email}
            },
        ]
    },
    {
        path: 'success',
        component: AuthCodeSuccessComponent
    },
    {
        path: 'signup',
        component: SignupComponent,
        canActivate: [GuestGuard]
    },
    {
        path: 'forgot-password',
        children: [
            {
                path: '',
                component: ResetOptionsComponent,
            },
            {
                path: 'email',
                component: AuthCodeComponent,
                data: { mode: AuthMode.reset, method: AuthMethod.email },
            },
            {
                path: 'email/:id',
                component: AuthCodeComponent,
                data: { mode: AuthMode.reset, method: AuthMethod.email },
            },
            {
                path: 'sms',
                component: AuthCodeComponent,
                data: { mode: AuthMode.reset, method: AuthMethod.sms }
            }
        ]
    },
    {
        path: 'reset-password',
        component: ResetPasswordComponent
    },
    {
        path: 'verify',
        children: [
            {
                path: 'email',
                component: AuthCodeComponent,
                data: { mode: AuthMode.verify, method: AuthMethod.email }
            },
            {
                path: 'phone',
                component: AuthCodeComponent,
                canActivate: [AuthGuard],
                data: { mode: AuthMode.verify, method: AuthMethod.sms }
            }
        ]
    },
    {
        path: 'accept-invitation',
        component: AcceptInvitationComponent
        // No guard - anyone with a valid token should be able to access
    },
    {
        path: 'unauthorized',
        component: UnauthorizedComponent
    },
    {
        path: 'account-not-supported',
        component: AccountNotSupportedComponent,
        canActivate: [AuthGuard]
    },

    // Public account-deletion landing page — NO guard. This is the URL
    // pasted into Google Play Console → App content → Data deletion to
    // satisfy Play's data-deletion-disclosure requirement. It must be
    // reachable without login so users and Play reviewers can find the
    // deletion instructions without holding valid credentials. See
    // agent-os/specs/2026-05-29-account-deletion/store-copy.md § A.
    // Production URL: https://app.americandronenetwork.com/account-deletion
    {
        path: 'account-deletion',
        component: AccountDeletionPageComponent
    },

    // CropSwipe mobile payment-return bouncer — public, no guard. Stripe
    // redirects the mobile app's in-app browser (Custom Tab /
    // SafariViewController) here after a payment flow. The page just says
    // "you're done, close to return" with ADN brand. Web's own post-payment
    // routes (e.g., /map-analysis/purchase/return) are separate + auth'd.
    // See billing-payments § 1.0.1 (Return URL Strategy / Path 1).
    {
        path: 'cropswipe/payments/return/:flow',
        loadComponent: () =>
            import('./pages/payments-return/payments-return.component')
                .then((m) => m.PaymentsReturnComponent),
    },
    {
        path: 'cropswipe/payments/return/:flow/cancel',
        loadComponent: () =>
            import('./pages/payments-return/payments-return.component')
                .then((m) => m.PaymentsReturnComponent),
    },

    // Protected routes (require authentication + organization membership)
    {
        path: '',
        component: LayoutComponent,
        canActivate: [AuthGuard, MembershipGuard],
        children: [
            {
                path: '',
                redirectTo: 'dashboard',
                pathMatch: 'full'
            },
            {
                path: 'dashboard',
                component: DashboardComponent
            },
            {
                path: 'activity',
                component: ActivityComponent
            },
            {
                path: 'company-profile',
                component: CompanyProfileComponent
            },
            {
                path: 'certifications',
                component: CertificationsComponent
            },
            {
                path: 'certifications/add',
                component: CertificationFormComponent
            },
            {
                path: 'certifications/:id/edit',
                component: CertificationFormComponent
            },
            // Team management route
            {
                path: 'team',
                component: TeamComponent
            },
            // Profile route
            {
                path: 'profile',
                component: ProfileComponent
            },
            // Account deletion (deactivate-and-retain). Login-gated mirror
            // of the mobile DeleteAccountScreen. See
            // agent-os/specs/2026-05-29-account-deletion/spec.md.
            // The public no-login landing page lives at /account-deletion
            // (top-level, used as the Play Console deletion URL).
            {
                path: 'profile/delete-account',
                component: DeleteAccountComponent
            },
            // Customer routes (top-level account entity)
            {
                path: 'customers',
                component: CustomersComponent
            },
            {
                path: 'customers/add',
                component: CustomerFormComponent
            },
            {
                path: 'customers/:id/view',
                component: CustomerDetailsComponent
            },
            {
                path: 'customers/:id/edit',
                component: CustomerFormComponent
            },
            // Work Order routes — Owner + Chief Supervisor only
            {
                path: 'work-orders',
                component: WorkOrdersComponent,
                canActivate: [MembershipRoleGuard],
                data: { membershipRoles: [MembershipRoles.Owner, MembershipRoles.ChiefSupervisor] }
            },
            {
                path: 'work-orders/new',
                component: WorkOrderFormComponent,
                canActivate: [MembershipRoleGuard],
                data: { membershipRoles: [MembershipRoles.Owner, MembershipRoles.ChiefSupervisor] }
            },
            {
                path: 'work-orders/:id',
                component: WorkOrderDetailsComponent,
                canActivate: [MembershipRoleGuard],
                data: { membershipRoles: [MembershipRoles.Owner, MembershipRoles.ChiefSupervisor] }
            },
            // Drone routes (top-level account entity)
            {
                path: 'drones',
                component: DronesComponent
            },
            {
                path: 'drones/add',
                component: DroneFormComponent
            },
            {
                path: 'drones/:id/view',
                component: DroneDetailsComponent
            },
            {
                path: 'drones/:id/edit',
                component: DroneFormComponent
            },
            // Logbook routes
            {
                path: 'logbook',
                component: LogbookOverviewComponent,
                pathMatch: 'full'
            },
            {
                path: 'logbook/spray-records',
                component: SprayRecordsComponent
            },
            {
                path: 'logbook/spray-records/add',
                component: SprayRecordFormComponent
            },
            {
                path: 'logbook/spray-records/:id/edit',
                component: SprayRecordFormComponent
            },
            {
                path: 'logbook/spray-records/:id/view',
                component: SprayRecordDetailsComponent
            },
            {
                path: 'logbook/chemicals/book',
                component: ChemicalBookComponent
            },
            {
                path: 'logbook/chemicals/custom',
                component: CustomChemicalsComponent
            },
            {
                path: 'logbook/chemicals/custom/add',
                component: CustomChemicalFormComponent
            },
            {
                path: 'logbook/chemicals/custom/:id/edit',
                component: CustomChemicalFormComponent
            },
            {
                path: 'logbook/chemicals/custom/:id/view',
                component: CustomChemicalDetailsComponent
            },
            {
                path: 'logbook/chemicals/templates',
                component: ChemicalTemplatesComponent
            },
            {
                path: 'logbook/chemicals/templates/add',
                component: ChemicalTemplateFormComponent
            },
            {
                path: 'logbook/chemicals/templates/:id/view',
                component: ChemicalTemplateDetailsComponent
            },
            {
                path: 'logbook/chemicals/templates/:id/edit',
                component: ChemicalTemplateFormComponent
            },
            // Testing routes
            {
                path: 'logbook/testing/safety',
                component: SafetyTestsComponent
            },
            {
                path: 'logbook/testing/safety/:testId/report',
                component: SafetyTestReportComponent
            },
            {
                path: 'logbook/testing/safety/:testId/take',
                component: SafetyTestTakeComponent
            },
            {
                path: 'logbook/testing/safety/custom/add',
                component: CustomSafetyTestFormComponent
            },
            {
                path: 'logbook/testing/safety/custom/:id/edit',
                component: CustomSafetyTestFormComponent
            },
            {
                path: 'logbook/testing/team',
                component: TeamReportComponent
            },
            {
                path: 'logbook/testing/field',
                component: FieldTestsComponent
            },
            {
                path: 'logbook/testing/field/add',
                component: FieldTestFormComponent
            },
            {
                path: 'logbook/testing/field/:id',
                component: FieldTestDetailsComponent
            },
            {
                path: 'logbook/testing/field/:id/edit',
                component: FieldTestFormComponent
            },
            {
                path: 'logbook/testing/field/custom/add',
                component: CustomFieldTestFormComponent
            },
            {
                path: 'logbook/testing/field/custom/:id',
                component: CustomFieldTestDetailsComponent
            },
            {
                path: 'logbook/testing/field/custom/:id/edit',
                component: CustomFieldTestFormComponent
            },
            // Fields routes
            {
                path: 'logbook/fields',
                component: FieldsComponent
            },
            {
                path: 'logbook/fields/add',
                component: FieldFormComponent
            },
            {
                path: 'logbook/fields/:id/edit',
                component: FieldFormComponent
            },
            // Reports routes
            {
                path: 'logbook/reports/faa',
                component: FaaReportsComponent
            },
            {
                path: 'logbook/reports/state',
                component: StateReportsComponent
            },
            // Maintenance routes
            {
                path: 'maintenance',
                component: MaintenanceDashboardComponent
            },
            // Support/Training preview (ADN-2284, TG10) — no guard here,
            // it's the landing spot the guard below redirects to.
            {
                path: 'maintenance/preview',
                component: MaintenancePreviewComponent
            },
            // Pro-only (ADN-2281, TG7).
            {
                path: 'maintenance/support-tickets',
                component: SupportTicketsComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/maintenance/preview' }
            },
            {
                path: 'maintenance/support-tickets/new',
                component: SupportTicketFormComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/maintenance/preview' }
            },
            {
                path: 'maintenance/support-tickets/:id',
                component: SupportTicketFormComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/maintenance/preview' }
            },
            {
                path: 'maintenance/service-appointments',
                component: ServiceAppointmentsComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/maintenance/preview' }
            },
            {
                path: 'maintenance/service-appointments/new',
                component: ServiceAppointmentFormComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/maintenance/preview' }
            },
            {
                path: 'maintenance/service-appointments/:id',
                component: ServiceAppointmentDetailComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/maintenance/preview' }
            },
            {
                path: 'maintenance/scheduled',
                component: ScheduledMaintenanceComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/maintenance/preview' }
            },
            {
                path: 'maintenance/training-videos',
                component: TrainingVideosComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/maintenance/preview' }
            },
            // Acres Advantage route
            {
                path: 'acres-advantage',
                component: AcresAdvantageComponent
            },
            // Agremo preview (ADN-2284, TG10) — no guard here, it's the
            // landing spot the guard below redirects to; guarding it too
            // would be a redirect loop.
            {
                path: 'analyses/preview',
                component: AgremoPreviewComponent
            },
            // Agremo Analysis routes — Pro-only (ADN-2281, TG7).
            {
                path: 'analyses',
                component: AnalysesComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/analyses/preview' }
            },
            {
                path: 'analyses/new',
                component: AnalysisFormComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/analyses/preview' }
            },
            {
                path: 'analyses/:id/view',
                component: AnalysisDetailComponent,
                canActivate: [MembershipTierGuard],
                data: { requiredTier: 'pro', preview: '/analyses/preview' }
            },
            // Stripe Checkout return — matches the Hub's WEB_DEFAULT_PATHS.map-analysis
            // (billing-payments § 1.0.1 / § 9.4).
            {
                path: 'map-analysis/purchase/return',
                component: AnalysisCheckoutReturnComponent
            },
            // Billing pages (ADN-2282/2283, TG8/TG9) — each component
            // handles its own Core/Pro redirect internally (tasks.md 8.5/9.4):
            // Upgrade to Pro (Core-only) <-> Manage Subscription (Pro-only)
            // redirect to each other for the wrong tier. No MembershipTierGuard
            // here — that guard protects Pro-*required* routes; this is the
            // inverse case.
            {
                path: 'billing/upgrade',
                component: UpgradeToProComponent
            },
            {
                path: 'billing',
                component: ManageSubscriptionComponent
            },
            // Stripe Checkout return for the annual subscription — matches
            // the Hub's WEB_DEFAULT_PATHS.subscription default in
            // user-payments-controller.ts's buildReturnUrls.
            {
                path: 'billing/subscription/return',
                component: SubscriptionCheckoutReturnComponent
            },
            // Future routes will be added here:
            // { path: 'drones', loadChildren: () => import('./features/drones/drones.module').then(m => m.DronesModule) },
            // { path: 'flight-logs', loadChildren: () => import('./features/flight-logs/flight-logs.module').then(m => m.FlightLogsModule) },
            // { path: 'account', loadChildren: () => import('./features/account/account.module').then(m => m.AccountModule) },
            // etc.
        ]
    },

    // Fallback route
    {
        path: '**',
        redirectTo: 'dashboard'
    }
]
